/**
 * Which Reserve notes one of the user's own private executions creates with a
 * limit carried over. "Redacted Reserve Privacy Indicator Exploration"
 * (XXXXXX, 2026-09-15): returning funds to the Reserve must not improve a
 * label, and deposits after a transfer cannot erase its link. So a note the
 * user's own move makes from limited funds stays limited, whatever its size:
 * - a merge's new note, when either note it spends was limited;
 * - a spend's change (withdraw, transfer to a sub), when the note it spends
 *   was limited;
 * - a return from Spending, when the asset was limited (a balance with
 *   nothing in the Reserve always is).
 * Deposits from a public wallet start fresh. Only the public wire messages
 * are read; the session keeps the result in memory and never stores or sends
 * it.
 */

const field = (value: unknown, key: string): unknown =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)[key]
    : undefined;

const text = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

/** Commitments of the new Reserve notes that keep a limit. */
export const carriedLimits = (
  messages: readonly unknown[],
  wasLimited: {
    /** The note spent with this nullifier was limited. */
    note: (nullifier: string) => boolean;
    /** The asset's funds in Spending were limited. */
    asset: (denom: string) => boolean;
  }
): string[] => {
  const carried: string[] = [];
  for (const message of messages) {
    const spend =
      field(message, "withdraw") ?? field(message, "transfer_to_subwallet");
    if (spend !== undefined) {
      const nullifier = field(spend, "nullifier");
      const output = field(spend, "output_commitment");
      if (text(nullifier) && text(output) && wasLimited.note(nullifier))
        carried.push(output);
      continue;
    }
    const merge = field(message, "merge_reserve_notes");
    if (merge !== undefined) {
      const inputs = [field(merge, "nullifier_a"), field(merge, "nullifier_b")];
      const output = field(merge, "output_commitment");
      if (
        text(output) &&
        inputs.some(
          (nullifier) => text(nullifier) && wasLimited.note(nullifier)
        )
      )
        carried.push(output);
      continue;
    }
    const shield = field(message, "shield_from_subwallet");
    if (shield !== undefined) {
      const denom = field(field(shield, "amount"), "denom");
      const output = field(shield, "reserve_commitment");
      if (text(denom) && text(output) && wasLimited.asset(denom))
        carried.push(output);
    }
  }
  return carried;
};
